import { trackEvent, trackEventGA4 } from '../gtm';
import { flatMap } from 'lodash';
import { trackDwhBetslipEventSubmit, trackDwhBetslipEventThirdParty } from '../../components/dwh-tracking/api';

interface Betslip {
    totals: any;
    betIds: any;
    betslipId: string;
    customerId: any;
    isPointsStake: boolean;
    type: 'Standard' | 'System';
    gameType: 'Prematch' | 'Live' | 'Hybrid' | 'Virtual';
    groups: Record<string, BetslipGroup>;
    submit?: {
        ticketId: string;
    };
    stake?: any;
}

interface BetslipGroup {
    picks: BetslipPick[];
}

interface BetslipPick {
    betId: string;
    sportId: string;
    sport: string;
    tournament: string;
    betEventId: string;
    marketId: string;
    market: string;
    home: string;
    away: string;
    header: string;
    selectionId: string;
    selectionCode: string;
    type: string;
}

export const trackBetslipCmd = (cmd?: any[], betslip?: Betslip) => {
    try {
        if (!cmd?.length || !betslip) {
            return;
        }

        const cmdType = cmd[0];

        switch (cmdType) {
            case 'AddPick':
            case 'AddPickMobile': {
                const { betId, positionCode, boxId } = cmd[2];

                const pick = findPickInBetslipByBetId(betslip, betId);

                if (!pick) {
                    return;
                }

                const eventActions = [
                    pick.type,
                    pick.sport,
                    pick.tournament,
                    pick.betEventId,
                    pick.market,
                    [pick.home, pick.away].join('_'),
                    boxId,
                    positionCode
                ];

                trackEvent('AddPick', eventActions.join(','), [pick.header, pick.selectionCode].join(','));
                trackEventGA4('add_pick', {
                    items: [pickToItem({ pick, positionCode })]
                });
                break;
            }
            case 'RemovePick': {
                const { betId } = cmd[2];

                const pick = findPickInBetslipByBetId(betslip, betId);

                if (!pick) {
                    return;
                }

                const eventActions = [
                    pick.type,
                    pick.sport,
                    pick.tournament,
                    pick.betEventId,
                    pick.market,
                    [pick.home, pick.away].join('_')
                ];

                trackEvent('RemovePick', eventActions.join(','), [pick.header, pick.selectionCode].join(','));
                trackEventGA4('remove_pick', {
                    items: [pickToItem({ pick })]
                });
            }
        }
    } catch (e) {
        console.error('Error in trackBetslipCmd: ', cmd, e);
    }
};

const getBetslipFromBetslips = (betslips: any[], index: number) => {
    const betslipsList = betslips.filter((betslip, _) => {
        return betslip.index === index;
    });
    return betslipsList[0];
};

export const trackThirdPartyCreateDraft = (cmd: any[], response: any) => {
    if (cmd[0] === 'CreateDraft' && cmd[2]?.positionCode === '3RD_LIVE_BANNER') {
        const temp = cmd[2].picks.Standard[0].split('-');
        const betslipIndex = response.betslipIndex;
        const betslip = getBetslipFromBetslips(response.betslips, betslipIndex);
        const cmdError = response?.lastCmdError;
        const error = betslip?.errors[0];

        if (cmdError || error) {
            trackDwhBetslipEventThirdParty(
                'add-third-party-pick-error',
                null,
                {
                    betId: temp[0],
                    selectionCode: temp[1]
                },
                cmdError || error
            );
        } else {
            trackDwhBetslipEventThirdParty('add-third-party-pick-success', null, {
                betId: temp[0],
                selectionCode: temp[1]
            });
        }
    }
};

export const trackBetslipSubmit = (betslip: Betslip) => {
    const ticketId = betslip.submit?.ticketId;
    const picks = flatMap(betslip.groups, group => flatMap(group.picks));
    const items = picks.map(pick => pickToItem({ pick }));
    const type = betslip.type;
    const stake = betslip.stake?.money;
    trackEvent('Editor tiketu', 'Tiket podany', getBetslipTrackingType(type));
    trackEventGA4('ticket_submitted', {
        ticketId,
        type,
        items
    });
    trackDwhBetslipEventSubmit(betslip.betslipId, ticketId, stake, betslip.gameType);
};

const findPickInBetslipByBetId = (betslip: Betslip, betId) => {
    if (!betslip) {
        return;
    }

    return Object.values(betslip.groups)
        .flatMap(group => group.picks)
        .find(p => p.betId === betId);
};

const getSportEventName = ({ home, away }) => [home, away].join(' - ');

const pickToItem = ({ pick, positionCode = '' }) => {
    const { sport, sportId, tournament, betEventId, home, away, marketId, market, selectionId, header, type } = pick;

    const sportEventName = getSportEventName({ home, away });
    const item_id = [sportId, betEventId, marketId, selectionId].join('|');
    const item_name = [sport, sportEventName, market, header].join('|');

    return {
        item_id: item_id,
        item_name: item_name,
        item_category: type,
        item_category2: sport,
        item_category3: tournament,
        item_category4: sportEventName,
        item_category5: market,
        location_id: positionCode
    };
};

const getBetslipTrackingType = type => {
    switch (type) {
        case 'Standard':
            return 'Jednoduchy';
        case 'System':
            return 'Rozpisovy';
        default:
            return '';
    }
};
